import React from 'react';
import { Helmet } from 'react-helmet';

/**
 * SEO enhancements for the Bangalore (SBC) -> Chennai (MAS) route page.
 *
 * This component is intentionally scoped to a single, fully curated route so that
 * the hand-written structured-data content (FAQs, train list, fares) is never
 * rendered on other route pages that share the generic RouteInfo component.
 *
 * All meta tags + LD+JSON schemas are injected via react-helmet (the same
 * mechanism HtmlHeader and BreadCrumb already use). Rendering this AFTER
 * HtmlHeader lets the title/description defined here win the helmet de-dupe.
 *
 * Existing BreadcrumbList and MobileApplication schemas (from BreadCrumb.tsx)
 * are left untouched.
 */

const PAGE_URL = 'https://www.makemytrip.com/railways/bangalore-chennai-train-tickets.html';

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Bangalore to Chennai Trains – Timetable, Fare & Booking',
  url: PAGE_URL,
  description:
    'Book Bangalore to Chennai train tickets. 30+ trains including Vande Bharat, Shatabdi Express, overnight trains. Check timetable, fares and availability.',
  inLanguage: 'en-IN',
  dateModified: '2026-06-18',
  isPartOf: {
    '@type': 'WebSite',
    name: 'MakeMyTrip',
    url: 'https://www.makemytrip.com'
  },
  about: {
    '@type': 'Thing',
    name: 'Bangalore to Chennai Train Route'
  },
  speakable: {
    '@type': 'SpeakableSpecification',
    cssSelector: [
      '#fare-range',
      '#night-trains',
      '#morning-trains',
      '#fastest-trains',
      '#tatkal',
      '#boarding-guide',
      '#arrival-guide',
      '#cancellation-refund',
      '#waitlist-guide',
      '#faqs'
    ]
  }
};

const faqPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is the fastest Bangalore to Chennai train?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'MAS Vande Bharat Express (20608) is the fastest, covering the route in 4 hours 30 minutes. It departs SBC at 14:50 and arrives MAS at 19:20. It offers Chair Car (CC) and Executive Chair Car (EC) classes and runs daily.'
      }
    },
    {
      '@type': 'Question',
      name: 'What is the best overnight train from Bangalore to Chennai?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The best overnight trains are: KSR Bengaluru–Chennai SF Mail (12658) departing 22:40, arriving 04:05, duration 5h 25m, daily, available in 1A/2A/3A/SL. Kaveri Express (16022) departing 23:50, arriving 06:45, duration 6h 55m, daily, good for families and budget travellers. Mysuru–Chennai Weekly SF (22681) departing 23:00, arriving 05:15, duration 6h 15m, runs on Wednesdays.'
      }
    },
    {
      '@type': 'Question',
      name: 'When does Tatkal booking open for Bangalore to Chennai trains?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Tatkal booking opens at 10:00 AM for AC classes (2A, 3A, CC, EC, 3E) and 11:00 AM for Sleeper and Non-AC classes, one day before the train departure date from the originating station. Demand is very high on Fridays, Sundays, festivals and long weekends — have passenger details, Aadhaar and payment ready before the window opens.'
      }
    },
    {
      '@type': 'Question',
      name: 'What are the Shatabdi Express timings from Bangalore to Chennai?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Shatabdi Express 12028 departs SBC at 06:00 and arrives MAS at 11:00 (5 hours), fare ₹945–₹1,535. Shatabdi Express 12008 departs SBC at 16:10 and arrives MAS at 21:25 (5 hours 15 minutes), fare ₹925–₹1,990. Both trains include meals and offer Chair Car (CC) and Executive Chair Car (EC) classes. Shatabdi does not have sleeping berths — it is a seated daytime service.'
      }
    },
    {
      '@type': 'Question',
      name: 'What is the Bangalore to Chennai train waitlist confirmation probability?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Confirmation chances by season: Peak (Apr–May, Sep–Oct): 60–70%. Off-peak (Jun–Aug): 80–90%. Normal (Jan–Mar, Nov–Dec): 70–80%. To achieve 85–90% confirmation: book 25–30 days in advance, choose Tuesday or Wednesday departures, select Sleeper or 3A class, choose less-demand trains like Bagmati Express or Lalbagh SF Express, and prefer GNWL over RLWL or PQWL.'
      }
    },
    {
      '@type': 'Question',
      name: 'What are the morning trains from Bangalore to Chennai?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Morning trains before 10 AM: Shatabdi Express (12028) at 06:00 arriving 11:00, CC/EC. Lalbagh SF Express (12608) at 06:25 arriving 12:20, 2A/3A/SL/CC/2S. MAS Vande Bharat (20663) at 07:45 arriving 12:30, CC/EC. Chennai Express (16552) at 07:55 arriving 14:35, 2S/CC. Sanghamitra Express (12295) at 09:00 arriving 15:15, 2A/3A/SL. SMVB KYQ AC Express (12551) at 09:30 arriving 14:40, 2A/3A.'
      }
    },
    {
      '@type': 'Question',
      name: 'What is the Sleeper class fare from Bangalore to Chennai?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Sleeper (SL) class fares on Bangalore to Chennai trains range between ₹230 and ₹900 depending on train type, booking window, travel season and availability. Budget trains like Kaveri Express (16022), Lalbagh SF Express (12608) and SBC MAS SF Mail (12658) usually offer the lowest Sleeper fares.'
      }
    },
    {
      '@type': 'Question',
      name: 'Which Bangalore station is best for Chennai trains — SBC, KJM or YPR?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'SBC (KSR Bengaluru City Junction) is best for maximum train availability and is recommended for South, West and Central Bangalore passengers. KJM (Krishnarajapuram) saves 1–2 hours of city traffic for East Bangalore passengers from Whitefield, ITPL, Marathahalli and KR Puram. YPR (Yesvantpur) is best for North Bangalore passengers from Hebbal, Yeshwanthpur and Tumkur Road. BNC (Bengaluru Cantonment) serves Indiranagar, HAL and Frazer Town for select trains only.'
      }
    },
    {
      '@type': 'Question',
      name: 'Which Chennai station do Bangalore trains arrive at — MAS or MS?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Most daily Bangalore to Chennai trains including Vande Bharat, Shatabdi Express, SBC MAS SF Mail and Lalbagh SF Express arrive at MAS (MGR Chennai Central). A few weekly and special trains arrive at MS (Chennai Egmore). MAS is better for North Chennai, OMR and Airport via Metro. MS is better for Tambaram, Velachery and South Chennai via MRTS. Always verify your specific train's arrival station on IRCTC before booking."
      }
    },
    {
      '@type': 'Question',
      name: 'Can I change my boarding station after booking?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. IRCTC allows a one-time boarding station change online before departure, provided the new station is a scheduled halt of that train. After the change, you lose boarding rights at the original station. The change must be done before chart preparation, approximately 4 hours before departure.'
      }
    },
    {
      '@type': 'Question',
      name: 'How can I check coach position before arrival?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Coach position for your train can be checked on the MakeMyTrip app, NTES app (National Train Enquiry System), or RailYatri app. You can also ask at the station inquiry counter 30–60 minutes before departure. Coach position boards are displayed on station platforms 15–20 minutes before departure.'
      }
    },
    {
      '@type': 'Question',
      name: 'What happens if my train ticket remains waitlisted?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'If your e-ticket remains waitlisted after chart preparation (4 hours before departure), it is automatically cancelled and a full refund is processed to your payment source. You cannot board the train with an unconfirmed waitlisted e-ticket. If you have opted for Trip Assurance you will get a relevant refund and voucher for booking alternate modes of travel.'
      }
    },
    {
      '@type': 'Question',
      name: 'Can I board from KJM instead of SBC?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, if your train has a scheduled halt at KJM and you update your boarding point on IRCTC before departure. The change is allowed once. If not updated, your berth may be marked vacant after the train departs SBC.'
      }
    },
    {
      '@type': 'Question',
      name: 'What is the difference between 2A and 3A class in trains?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '2A (Second AC) has 4 berths per bay with side curtains for privacy, fares of ₹880–₹2,000 on this route. 3A (Third AC) has 6 berths per bay, is more affordable at ₹625–₹1,100, and is ideal for students and solo travellers. Both classes include AC and charging points.'
      }
    },
    {
      '@type': 'Question',
      name: 'How much refund do I get if I cancel my Bangalore to Chennai train ticket?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'For confirmed tickets: 48+ hours before departure — full refund minus flat cancellation charge. 12–48 hours before — 25% deducted. 4–12 hours before — 50% deducted. No refund after chart preparation. Tatkal tickets have stricter no-refund rules.'
      }
    },
    {
      '@type': 'Question',
      name: 'What is a waitlist ticket and will it confirm?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A waitlist ticket is a non-confirmed reservation that moves up as cancellations happen. GNWL tickets have 60–85% confirmation probability on the Bangalore to Chennai route. Most confirmations happen 1–5 days before departure. Unconfirmed e-tickets are automatically cancelled after chart preparation.'
      }
    },
    {
      '@type': 'Question',
      name: 'Can I select my seat or berth preference?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. IRCTC allows berth preference selection (lower, middle, upper, side lower, side upper) during booking, subject to availability. Lower berths are automatically allotted to senior citizens (60+) and passengers with disabilities. For others, availability depends on inventory at the time of booking.'
      }
    },
    {
      '@type': 'Question',
      name: 'Are Tatkal tickets available for Bangalore to Chennai trains?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Tatkal opens at 10:00 AM for AC classes (2A, 3A, CC, EC) and 11:00 AM for Non-AC classes, one day before departure. Demand is very high on this route — have all passenger details and payment ready before the window opens.'
      }
    },
    {
      '@type': 'Question',
      name: 'What happens if my Bangalore to Chennai train is cancelled?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Full automatic refund to your payment source for e-tickets. No TDR filing is needed for train cancellations. Counter tickets can be refunded at station within 3 days of the scheduled departure.'
      }
    },
    {
      '@type': 'Question',
      name: 'Is online booking safe for Bangalore to Chennai trains?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. IRCTC is the official Indian Railways booking platform and is fully secure. MakeMyTrip is an IRCTC-authorised partner with 256-bit SSL encryption and multiple secure payment options.'
      }
    },
    {
      '@type': 'Question',
      name: 'How do I check my PNR status?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Check PNR status on the IRCTC website, MakeMyTrip app, or by calling 139 (Indian Railways helpline). Your 10-digit PNR is printed on your ticket. Status updates are most frequent 5–10 days before departure.'
      }
    },
    {
      '@type': 'Question',
      name: 'What if I miss my Bangalore to Chennai train?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'File a TDR (Ticket Deposit Receipt) at the station within 3 hours of scheduled departure for a potential refund. For e-tickets, TDR can be filed online on IRCTC. Tatkal tickets generally do not qualify for TDR refunds.'
      }
    },
    {
      '@type': 'Question',
      name: 'Can families book side-by-side seats on Bangalore to Chennai trains?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Book all passengers in one transaction and select berth preferences. In 3A, booking a full 6-berth bay together works best for families. In 2A, side lower berths offer extra privacy. Early booking significantly improves chances of getting adjacent berths.'
      }
    },
    {
      '@type': 'Question',
      name: 'Which train is best for overnight travel from Bangalore to Chennai?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'KSR Bengaluru–Chennai SF Mail (12658) is the fastest overnight option (5h 25m, departs 22:40, arrives 04:05). Kaveri Express (16022) is best for a comfortable sleep window (departs 23:50, arrives 06:45). Both run daily and are available in Sleeper, 3A, 2A and 1A classes.'
      }
    },
    {
      '@type': 'Question',
      name: 'Can I get a lower berth specifically on Bangalore to Chennai trains?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Lower berths can be requested as a preference during booking on IRCTC. They are automatically allotted to senior citizens (60+) and passengers with disabilities. For other passengers, availability depends on inventory at the time of booking.'
      }
    },
    {
      '@type': 'Question',
      name: 'Is food included in Bangalore to Chennai train ticket price?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Meals are included in ticket prices for Shatabdi Express and Vande Bharat Express trains. For other trains like SF Mail, Kaveri Express and Sanghamitra Express, meals are not included but pantry cars or food vendors are usually available on board.'
      }
    },
    {
      '@type': 'Question',
      name: 'What is a GNWL ticket?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'GNWL stands for General Waitlist — the most common and highest confirmation-probability waitlist category on Indian Railways. On Bangalore to Chennai trains, GNWL tickets confirm at a 60–85% rate depending on season. It is the preferred waitlist type over RLWL or PQWL.'
      }
    },
    {
      '@type': 'Question',
      name: 'How early should I reach the station for Bangalore to Chennai trains?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Arrive at least 30 minutes before departure for express trains. For SBC, allow extra time for Majestic area traffic. For KJM, 20–25 minutes is usually sufficient. Platform and coach position boards are displayed 15–20 minutes before departure.'
      }
    },
    {
      '@type': 'Question',
      name: 'What is the best time to book Bangalore to Chennai train tickets?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The Advance Reservation Period (ARP) opens 60 days before travel. Booking immediately when ARP opens gives the best confirmed seat availability. For peak periods like Pongal, Deepavali and summer holidays, booking 45–60 days in advance is strongly recommended.'
      }
    },
    {
      '@type': 'Question',
      name: 'What if my waitlist ticket does not confirm?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'If your e-ticket remains waitlisted after chart preparation, it is automatically cancelled and a full refund is processed within 3–7 working days to your original payment method. Consider using Tatkal booking on an alternate train as a backup.'
      }
    },
    {
      '@type': 'Question',
      name: 'Does Chennai Central station have Metro connectivity?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. MGR Chennai Central (MAS) has direct Chennai Metro connectivity via the Blue Line from Chennai Central Metro Station, integrated with the railway station. The Metro connects to Chennai Airport, OMR, Guindy, CMBT and other key city destinations.'
      }
    },
    {
      '@type': 'Question',
      name: 'Is it safe for women to travel alone on Bangalore to Chennai trains?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Yes. Bangalore to Chennai trains have dedicated ladies' compartments, RPF (Railway Protection Force) personnel at all major stations, and 24/7 CCTV surveillance. The Meri Saheli initiative provides RPF female escorts for solo women travellers on select trains. Women can call RPF helpline 182 from any station or train. Pre-booking confirmed berths and cabs for both ends of the journey is recommended for late-night travel."
      }
    },
    {
      '@type': 'Question',
      name: 'Is it safe for women to arrive alone at Chennai Central at night?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Yes. MGR Chennai Central (MAS) has 24/7 RPF presence, full CCTV surveillance, a dedicated women's waiting room and prepaid cab counters inside the station premises. The Meri Saheli programme covers arrivals at MAS. Pre-booking a cab and using Chennai Metro (operational till midnight) are recommended for solo women travellers arriving at night. RPF helpline 182 is available at any time."
      }
    }
  ]
};

const howToSchema = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to Get a Confirmed Ticket on Bangalore to Chennai Trains',
  description: 'Steps to maximise confirmation chances for Bangalore to Chennai train tickets',
  step: [
    {
      '@type': 'HowToStep',
      position: '1',
      name: 'Book 25–30 days in advance',
      text: 'The Advance Reservation Period opens 60 days before travel. Booking 25–30 days in advance gives the best confirmed seat availability on this high-demand route.'
    },
    {
      '@type': 'HowToStep',
      position: '2',
      name: 'Choose Tuesday or Wednesday departures',
      text: 'Mid-week trains have significantly lower demand compared to Friday evenings and Sunday trains, offering 15–20% better confirmation probability.'
    },
    {
      '@type': 'HowToStep',
      position: '3',
      name: 'Select GNWL waitlist category',
      text: 'GNWL (General Waitlist) has the highest confirmation probability of 60–85%. Avoid RLWL which has very low confirmation chances.'
    },
    {
      '@type': 'HowToStep',
      position: '4',
      name: 'Choose Sleeper or 3A class',
      text: 'These classes have the largest seat inventory on most trains, giving better confirmation odds.'
    },
    {
      '@type': 'HowToStep',
      position: '5',
      name: 'Consider alternate boarding stations',
      text: 'Check availability from alternate boarding stations like KJM or YPR if SBC trains are heavily booked.'
    },
    {
      '@type': 'HowToStep',
      position: '6',
      name: 'Use Tatkal for urgent travel',
      text: 'Tatkal booking opens at 10:00 AM (AC classes) or 11:00 AM (Non-AC) one day before departure. Have passenger details, Aadhaar and payment ready before the opening time.'
    }
  ]
};

const itemListSchema = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Popular Bangalore to Chennai Trains',
  description: 'Most popular trains operating between Bangalore and Chennai with timings, duration and fare',
  numberOfItems: 10,
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      item: {
        '@type': 'Product',
        name: 'KSR Bengaluru – Chennai SF Mail (12658)',
        description:
          'Fastest daily overnight train. Departs SBC at 22:40, arrives MAS at 04:05. Duration 5h 25m. Classes: 1A, 2A, 3A, SL.',
        offers: { '@type': 'AggregateOffer', lowPrice: '230', highPrice: '5500', priceCurrency: 'INR' }
      }
    },
    {
      '@type': 'ListItem',
      position: 2,
      item: {
        '@type': 'Product',
        name: 'MAS Vande Bharat Express (20608)',
        description:
          'Fastest Bangalore to Chennai train. Departs SBC at 14:50, arrives MAS at 19:20. Duration 4h 30m. Classes: CC, EC.',
        offers: { '@type': 'AggregateOffer', lowPrice: '550', highPrice: '2000', priceCurrency: 'INR' }
      }
    },
    {
      '@type': 'ListItem',
      position: 3,
      item: {
        '@type': 'Product',
        name: 'Shatabdi Express (12028)',
        description:
          'Morning Shatabdi. Departs SBC at 06:00, arrives MAS at 11:00. Duration 5h. Classes: CC, EC. Meals included.',
        offers: { '@type': 'AggregateOffer', lowPrice: '945', highPrice: '1535', priceCurrency: 'INR' }
      }
    },
    {
      '@type': 'ListItem',
      position: 4,
      item: {
        '@type': 'Product',
        name: 'Shatabdi Express (12008)',
        description:
          'Evening Shatabdi. Departs SBC at 16:10, arrives MAS at 21:25. Duration 5h 15m. Classes: CC, EC. Meals included.',
        offers: { '@type': 'AggregateOffer', lowPrice: '925', highPrice: '1990', priceCurrency: 'INR' }
      }
    },
    {
      '@type': 'ListItem',
      position: 5,
      item: {
        '@type': 'Product',
        name: 'Kaveri Express (16022)',
        description:
          'Popular overnight train for families and budget travellers. Departs SBC at 23:50, arrives MAS at 06:45. Duration 6h 55m. Classes: 1A, 2A, 3A, SL.',
        offers: { '@type': 'AggregateOffer', lowPrice: '230', highPrice: '2000', priceCurrency: 'INR' }
      }
    },
    {
      '@type': 'ListItem',
      position: 6,
      item: {
        '@type': 'Product',
        name: 'Lalbagh SF Express (12608)',
        description:
          'Popular morning train. Departs SBC at 06:25, arrives MAS at 12:20. Duration 5h 55m. Classes: 2A, 3A, SL, CC, 2S.',
        offers: { '@type': 'AggregateOffer', lowPrice: '130', highPrice: '1600', priceCurrency: 'INR' }
      }
    },
    {
      '@type': 'ListItem',
      position: 7,
      item: {
        '@type': 'Product',
        name: 'MAS Vande Bharat Express (20663)',
        description:
          'Morning Vande Bharat. Departs SBC at 07:45, arrives MAS at 12:30. Duration 4h 45m. Classes: CC, EC.',
        offers: { '@type': 'AggregateOffer', lowPrice: '550', highPrice: '2000', priceCurrency: 'INR' }
      }
    },
    {
      '@type': 'ListItem',
      position: 8,
      item: {
        '@type': 'Product',
        name: 'Sanghamitra Express (12295)',
        description:
          'Daily morning train. Departs SBC at 09:00, arrives MAS at 15:15. Duration 6h 15m. Classes: 2A, 3A, SL.',
        offers: { '@type': 'AggregateOffer', lowPrice: '230', highPrice: '1600', priceCurrency: 'INR' }
      }
    },
    {
      '@type': 'ListItem',
      position: 9,
      item: {
        '@type': 'Product',
        name: 'MAS Double Decker (22626)',
        description:
          'Budget daytime train. Departs SBC at 13:30, arrives MAS at 19:45. Duration 6h 15m. Classes: CC, 2S.',
        offers: { '@type': 'AggregateOffer', lowPrice: '130', highPrice: '1400', priceCurrency: 'INR' }
      }
    },
    {
      '@type': 'ListItem',
      position: 10,
      item: {
        '@type': 'Product',
        name: 'SMET MAS SF Express (12692)',
        description:
          'Late-night business train. Departs SBC at 23:00, arrives MAS at 04:55. Duration 5h 55m. Classes: 2A, 3A, SL.',
        offers: { '@type': 'AggregateOffer', lowPrice: '230', highPrice: '1600', priceCurrency: 'INR' }
      }
    }
  ]
};

const datasetSchema = {
  '@context': 'https://schema.org',
  '@type': 'Dataset',
  name: 'Bangalore to Chennai Train Fare by Class',
  description: 'Fare ranges for all travel classes on Bangalore to Chennai trains',
  variableMeasured: [
    {
      '@type': 'PropertyValue',
      name: 'General / 2S (Second Sitting)',
      value: '₹130–₹170',
      description: 'Most economical class. Available on trains like Lalbagh SF Express.'
    },
    {
      '@type': 'PropertyValue',
      name: 'SL (Sleeper Class)',
      value: '₹230–₹900',
      description: 'Non-AC berths. Most popular for budget overnight travel.'
    },
    {
      '@type': 'PropertyValue',
      name: '3A (Third AC)',
      value: '₹625–₹1,100',
      description: 'Air-conditioned 6-berth bays. Best value AC option.'
    },
    {
      '@type': 'PropertyValue',
      name: '2A (Second AC)',
      value: '₹880–₹2,000',
      description: 'Air-conditioned 4-berth bays with curtains. Preferred by families.'
    },
    {
      '@type': 'PropertyValue',
      name: 'CC (Chair Car)',
      value: '₹550–₹1,400',
      description: 'AC seated class for daytime trains like Shatabdi and Vande Bharat.'
    },
    {
      '@type': 'PropertyValue',
      name: 'EC (Executive Chair Car)',
      value: '₹1,500–₹2,000',
      description: 'Premium AC seating on Shatabdi and Vande Bharat trains.'
    },
    {
      '@type': 'PropertyValue',
      name: '1A (First AC)',
      value: '₹4,000–₹5,500',
      description: 'Premium private cabins. Available on overnight trains like SF Mail.'
    }
  ]
};

const nightTrainsSchema = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Best Night Trains from Bangalore to Chennai',
  description: 'Overnight trains from Bangalore (SBC) to Chennai (MAS) — fastest options, sleep window and frequency',
  numberOfItems: 3,
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      item: {
        '@type': 'Product',
        name: 'KSR Bengaluru – Chennai SF Mail (12658)',
        description:
          'Fastest daily overnight train. Departs SBC at 22:40, arrives MAS at 04:05. Duration 5h 25m. Runs daily. Best for fastest overnight travel.',
        offers: { '@type': 'AggregateOffer', lowPrice: '230', highPrice: '5500', priceCurrency: 'INR' }
      }
    },
    {
      '@type': 'ListItem',
      position: 2,
      item: {
        '@type': 'Product',
        name: 'Kaveri Express (16022)',
        description:
          'Comfortable overnight train. Departs SBC at 23:50, arrives MAS at 06:45. Duration 6h 55m. Runs daily. Best for a longer sleep window.',
        offers: { '@type': 'AggregateOffer', lowPrice: '230', highPrice: '2000', priceCurrency: 'INR' }
      }
    },
    {
      '@type': 'ListItem',
      position: 3,
      item: {
        '@type': 'Product',
        name: 'Mysuru – Chennai Weekly SF (22681)',
        description:
          'Weekly overnight train. Departs at 23:00, arrives at 05:15. Duration 6h 15m. Runs on Wednesdays. Useful backup when daily trains are full.',
        offers: { '@type': 'AggregateOffer', lowPrice: '230', highPrice: '2000', priceCurrency: 'INR' }
      }
    }
  ]
};

const morningTrainsSchema = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Best Morning Trains from Bangalore to Chennai',
  description:
    'Morning trains departing before 10 AM from Bangalore (SBC) to Chennai (MAS) — timings, duration and classes',
  numberOfItems: 6,
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      item: {
        '@type': 'Product',
        name: 'Shatabdi Express (12028)',
        description: 'Departs SBC at 06:00, arrives MAS at 11:00. Duration 5h. Classes: CC, EC. Meals included.',
        offers: { '@type': 'AggregateOffer', lowPrice: '945', highPrice: '1535', priceCurrency: 'INR' }
      }
    },
    {
      '@type': 'ListItem',
      position: 2,
      item: {
        '@type': 'Product',
        name: 'Lalbagh SF Express (12608)',
        description:
          'Departs SBC at 06:25, arrives MAS at 12:20. Duration 5h 55m. Classes: 2A, 3A, SL, CC, 2S. Widest class range.',
        offers: { '@type': 'AggregateOffer', lowPrice: '130', highPrice: '1600', priceCurrency: 'INR' }
      }
    },
    {
      '@type': 'ListItem',
      position: 3,
      item: {
        '@type': 'Product',
        name: 'MAS Vande Bharat (20663)',
        description:
          'Departs SBC at 07:45, arrives MAS at 12:30. Duration 4h 45m. Classes: CC, EC. Fastest morning option.',
        offers: { '@type': 'AggregateOffer', lowPrice: '550', highPrice: '2000', priceCurrency: 'INR' }
      }
    },
    {
      '@type': 'ListItem',
      position: 4,
      item: {
        '@type': 'Product',
        name: 'Chennai Express (16552)',
        description: 'Departs SBC at 07:55, arrives MAS at 14:35. Duration 6h 40m. Classes: 2S, CC.',
        offers: { '@type': 'AggregateOffer', lowPrice: '130', highPrice: '1400', priceCurrency: 'INR' }
      }
    },
    {
      '@type': 'ListItem',
      position: 5,
      item: {
        '@type': 'Product',
        name: 'Sanghamitra Express (12295)',
        description: 'Departs SBC at 09:00, arrives MAS at 15:15. Duration 6h 15m. Classes: 2A, 3A, SL.',
        offers: { '@type': 'AggregateOffer', lowPrice: '230', highPrice: '1600', priceCurrency: 'INR' }
      }
    },
    {
      '@type': 'ListItem',
      position: 6,
      item: {
        '@type': 'Product',
        name: 'SMVB KYQ AC Express (12551)',
        description: 'Departs SBC at 09:30, arrives MAS at 14:40. Duration 5h 10m. Classes: 2A, 3A.',
        offers: { '@type': 'AggregateOffer', lowPrice: '625', highPrice: '2000', priceCurrency: 'INR' }
      }
    }
  ]
};

const fastestTrainsSchema = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Fastest Trains from Bangalore to Chennai — Shatabdi and Vande Bharat',
  description: 'Fastest trains on the Bangalore to Chennai route with timings, duration and fare range',
  numberOfItems: 3,
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      item: {
        '@type': 'Product',
        name: 'MAS Vande Bharat Express (20608) — Fastest Train',
        description:
          'Fastest Bangalore to Chennai train. Departs SBC at 14:50, arrives MAS at 19:20. Duration 4h 30m. Classes: CC, EC. Daily. Best for daytime business travel.',
        offers: { '@type': 'AggregateOffer', lowPrice: '550', highPrice: '2000', priceCurrency: 'INR' }
      }
    },
    {
      '@type': 'ListItem',
      position: 2,
      item: {
        '@type': 'Product',
        name: 'Shatabdi Express (12028)',
        description:
          'Departs SBC at 06:00, arrives MAS at 11:00. Duration 5h. Classes: CC, EC. Fare ₹945–₹1,535. Meals included.',
        offers: { '@type': 'AggregateOffer', lowPrice: '945', highPrice: '1535', priceCurrency: 'INR' }
      }
    },
    {
      '@type': 'ListItem',
      position: 3,
      item: {
        '@type': 'Product',
        name: 'Shatabdi Express (12008)',
        description:
          'Departs SBC at 16:10, arrives MAS at 21:25. Duration 5h 15m. Classes: CC, EC. Fare ₹925–₹1,990. Meals included.',
        offers: { '@type': 'AggregateOffer', lowPrice: '925', highPrice: '1990', priceCurrency: 'INR' }
      }
    }
  ]
};

const cancellationHowToSchema = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to Cancel a Bangalore to Chennai Train Ticket and Get a Refund',
  description:
    'Step-by-step guide on cancellation refund rules for Bangalore to Chennai train tickets — timing, refund percentage and deductions by class',
  step: [
    {
      '@type': 'HowToStep',
      position: '1',
      name: 'Cancel 48+ hours before departure',
      text: 'Full refund minus a flat cancellation charge: ₹60 for Sleeper, ₹90 for 3A, ₹180 for 2A, CC and EC. This is the best window for maximum refund.'
    },
    {
      '@type': 'HowToStep',
      position: '2',
      name: 'Cancel 12–48 hours before departure',
      text: '75% refund (25% deducted). Minimum ₹60 deduction for Sleeper, ₹180 for AC classes.'
    },
    {
      '@type': 'HowToStep',
      position: '3',
      name: 'Cancel 4–12 hours before departure',
      text: '50% refund (50% deducted). Subject to minimum deduction amounts by class.'
    },
    {
      '@type': 'HowToStep',
      position: '4',
      name: 'Less than 4 hours or after chart preparation',
      text: 'No refund on online cancellation. TDR can be filed at the station in exceptional cases only.'
    },
    {
      '@type': 'HowToStep',
      position: '5',
      name: 'Waitlisted e-ticket (auto-cancelled)',
      text: 'Full refund minus ₹20–₹60 per passenger. Auto-refunded to original payment method within 3–7 working days. No manual action needed.'
    },
    {
      '@type': 'HowToStep',
      position: '6',
      name: 'Tatkal ticket cancellation',
      text: 'No refund on Tatkal cancellations. Exception: if Indian Railways cancels the train, a full refund is automatically processed.'
    }
  ]
};

const boardingStationHowToSchema = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to Choose the Right Boarding Station for Bangalore to Chennai Trains',
  description:
    'Guide to selecting the best boarding station (SBC, KJM, YPR or BNC) for Bangalore to Chennai trains based on your area in Bangalore',
  step: [
    {
      '@type': 'HowToStep',
      position: '1',
      name: 'East Bangalore — Choose KJM',
      text: 'If you are from Whitefield, ITPL, KR Puram, Marathahalli or Bellandur, board from Krishnarajapuram (KJM). This saves 1–2 hours of city traffic compared to SBC. Four daily trains stop at KJM: Kaveri Express, Lalbagh SF Express, Brindavan SF Express and MAS Double Decker.'
    },
    {
      '@type': 'HowToStep',
      position: '2',
      name: 'North Bangalore — Choose YPR',
      text: 'If you are from Hebbal, Yelahanka, Yeshwanthpur or Tumkur Road, board from Yesvantpur Junction (YPR). Avoids Majestic area traffic entirely.'
    },
    {
      '@type': 'HowToStep',
      position: '3',
      name: 'South, West or Central Bangalore — Choose SBC',
      text: 'Passengers from Jayanagar, BTM, JP Nagar, Electronic City, MG Road and Shivajinagar should board from KSR Bengaluru City Junction (SBC). It has the highest number of departures, metro connectivity and full station facilities.'
    },
    {
      '@type': 'HowToStep',
      position: '4',
      name: 'Central-East Bangalore — Check BNC',
      text: 'Bengaluru Cantonment (BNC) serves Indiranagar, HAL and Frazer Town for select trains only. Verify your specific train halts at BNC on IRCTC before planning to board here.'
    },
    {
      '@type': 'HowToStep',
      position: '5',
      name: 'Change boarding station on MakeMyTrip',
      text: 'To board from KJM or YPR instead of SBC, request a boarding point change on MakeMyTrip at least 24 hours before departure. The change is allowed once and cannot be reversed.'
    }
  ]
};

const tatkalDatasetSchema = {
  '@context': 'https://schema.org',
  '@type': 'Dataset',
  name: 'Bangalore to Chennai Tatkal Ticket Booking Details',
  description: 'Tatkal booking window timings, peak demand days and charges for Bangalore to Chennai trains',
  variableMeasured: [
    {
      '@type': 'PropertyValue',
      name: 'AC Classes Tatkal Opening Time',
      value: '10:00 AM',
      description: 'Tatkal booking opens at 10:00 AM for 2A, 3A, CC, EC, 3E classes — one day before departure'
    },
    {
      '@type': 'PropertyValue',
      name: 'Non-AC Classes Tatkal Opening Time',
      value: '11:00 AM',
      description: 'Tatkal booking opens at 11:00 AM for Sleeper and General classes — one day before departure'
    },
    {
      '@type': 'PropertyValue',
      name: 'Tatkal Booking Window',
      value: '1 day before departure',
      description: 'Counted from the originating station departure date'
    },
    {
      '@type': 'PropertyValue',
      name: 'Peak Demand Days',
      value: 'Fridays, Sundays, festivals, long weekends',
      description: 'Tuesday and Wednesday departures offer better Tatkal confirmation chances'
    }
  ]
};

const waitlistDatasetSchema = {
  '@context': 'https://schema.org',
  '@type': 'Dataset',
  name: 'Bangalore to Chennai Waitlist Confirmation Rates by Type',
  description: 'Waitlist confirmation probability by type for Bangalore to Chennai train tickets',
  variableMeasured: [
    {
      '@type': 'PropertyValue',
      name: 'GNWL (General Waitlist)',
      value: '60–85% confirmation chance',
      description: 'Best waitlist type — should be first choice. Highest confirmation probability on this route.'
    },
    {
      '@type': 'PropertyValue',
      name: 'PQWL (Pooled Quota Waitlist)',
      value: 'Moderate confirmation chance',
      description: 'Reasonable backup if GNWL is unavailable.'
    },
    {
      '@type': 'PropertyValue',
      name: 'RLWL (Remote Location Waitlist)',
      value: 'Very low confirmation chance',
      description: 'Avoid if possible — very low confirmation probability.'
    },
    {
      '@type': 'PropertyValue',
      name: 'RAC (Reservation Against Cancellation)',
      value: 'Very high confirmation chance',
      description: 'Travel is allowed even unconfirmed — shared berth assigned. Often upgrades to full berth.'
    }
  ]
};

const arrivalStationDatasetSchema = {
  '@context': 'https://schema.org',
  '@type': 'Dataset',
  name: 'Bangalore to Chennai Train Arrival Stations — MAS vs MS',
  description:
    'Comparison of Chennai arrival stations (MAS and MS) for Bangalore to Chennai trains by destination area',
  variableMeasured: [
    {
      '@type': 'PropertyValue',
      name: 'MAS (MGR Chennai Central)',
      value: 'Most daily trains, Metro connectivity',
      description: 'Best for: T Nagar, Anna Nagar, OMR, Chennai Airport, Parrys. Direct Blue Line Metro from station.'
    },
    {
      '@type': 'PropertyValue',
      name: 'MS (Chennai Egmore)',
      value: 'Select weekly and special trains',
      description: 'Best for: Tambaram, Chromepet, Velachery, Guindy, GST Road. MRTS suburban connectivity.'
    }
  ]
};

const trainTripSchemas = [
  {
    '@context': 'https://schema.org',
    '@type': 'TrainTrip',
    name: 'KSR Bengaluru – Chennai SF Mail',
    trainNumber: '12658',
    departureStation: { '@type': 'TrainStation', name: 'KSR Bengaluru City Junction', identifier: 'SBC' },
    arrivalStation: { '@type': 'TrainStation', name: 'MGR Chennai Central', identifier: 'MAS' },
    departureTime: '22:40',
    arrivalTime: '04:05',
    provider: { '@type': 'Organization', name: 'Indian Railways' },
    offers: { '@type': 'Offer', priceCurrency: 'INR', lowPrice: '230', highPrice: '5500' }
  },
  {
    '@context': 'https://schema.org',
    '@type': 'TrainTrip',
    name: 'MAS Vande Bharat Express',
    trainNumber: '20608',
    departureStation: { '@type': 'TrainStation', name: 'KSR Bengaluru City Junction', identifier: 'SBC' },
    arrivalStation: { '@type': 'TrainStation', name: 'MGR Chennai Central', identifier: 'MAS' },
    departureTime: '14:50',
    arrivalTime: '19:20',
    provider: { '@type': 'Organization', name: 'Indian Railways' },
    offers: { '@type': 'Offer', priceCurrency: 'INR', lowPrice: '550', highPrice: '2000' }
  },
  {
    '@context': 'https://schema.org',
    '@type': 'TrainTrip',
    name: 'Shatabdi Express',
    trainNumber: '12028',
    departureStation: { '@type': 'TrainStation', name: 'KSR Bengaluru City Junction', identifier: 'SBC' },
    arrivalStation: { '@type': 'TrainStation', name: 'MGR Chennai Central', identifier: 'MAS' },
    departureTime: '06:00',
    arrivalTime: '11:00',
    provider: { '@type': 'Organization', name: 'Indian Railways' },
    offers: { '@type': 'Offer', priceCurrency: 'INR', lowPrice: '945', highPrice: '1535' }
  },
  {
    '@context': 'https://schema.org',
    '@type': 'TrainTrip',
    name: 'Shatabdi Express',
    trainNumber: '12008',
    departureStation: { '@type': 'TrainStation', name: 'KSR Bengaluru City Junction', identifier: 'SBC' },
    arrivalStation: { '@type': 'TrainStation', name: 'MGR Chennai Central', identifier: 'MAS' },
    departureTime: '16:10',
    arrivalTime: '21:25',
    provider: { '@type': 'Organization', name: 'Indian Railways' },
    offers: { '@type': 'Offer', priceCurrency: 'INR', lowPrice: '925', highPrice: '1990' }
  },
  {
    '@context': 'https://schema.org',
    '@type': 'TrainTrip',
    name: 'Kaveri Express',
    trainNumber: '16022',
    departureStation: { '@type': 'TrainStation', name: 'KSR Bengaluru City Junction', identifier: 'SBC' },
    arrivalStation: { '@type': 'TrainStation', name: 'MGR Chennai Central', identifier: 'MAS' },
    departureTime: '23:50',
    arrivalTime: '06:45',
    provider: { '@type': 'Organization', name: 'Indian Railways' },
    offers: { '@type': 'Offer', priceCurrency: 'INR', lowPrice: '230', highPrice: '2000' }
  },
  {
    '@context': 'https://schema.org',
    '@type': 'TrainTrip',
    name: 'Lalbagh SF Express',
    trainNumber: '12608',
    departureStation: { '@type': 'TrainStation', name: 'KSR Bengaluru City Junction', identifier: 'SBC' },
    arrivalStation: { '@type': 'TrainStation', name: 'MGR Chennai Central', identifier: 'MAS' },
    departureTime: '06:25',
    arrivalTime: '12:20',
    provider: { '@type': 'Organization', name: 'Indian Railways' },
    offers: { '@type': 'Offer', priceCurrency: 'INR', lowPrice: '130', highPrice: '1600' }
  },
  {
    '@context': 'https://schema.org',
    '@type': 'TrainTrip',
    name: 'MAS Vande Bharat Express',
    trainNumber: '20663',
    departureStation: { '@type': 'TrainStation', name: 'KSR Bengaluru City Junction', identifier: 'SBC' },
    arrivalStation: { '@type': 'TrainStation', name: 'MGR Chennai Central', identifier: 'MAS' },
    departureTime: '07:45',
    arrivalTime: '12:30',
    provider: { '@type': 'Organization', name: 'Indian Railways' },
    offers: { '@type': 'Offer', priceCurrency: 'INR', lowPrice: '550', highPrice: '2000' }
  },
  {
    '@context': 'https://schema.org',
    '@type': 'TrainTrip',
    name: 'Sanghamitra Express',
    trainNumber: '12295',
    departureStation: { '@type': 'TrainStation', name: 'KSR Bengaluru City Junction', identifier: 'SBC' },
    arrivalStation: { '@type': 'TrainStation', name: 'MGR Chennai Central', identifier: 'MAS' },
    departureTime: '09:00',
    arrivalTime: '15:15',
    provider: { '@type': 'Organization', name: 'Indian Railways' },
    offers: { '@type': 'Offer', priceCurrency: 'INR', lowPrice: '230', highPrice: '1600' }
  },
  {
    '@context': 'https://schema.org',
    '@type': 'TrainTrip',
    name: 'MAS Double Decker',
    trainNumber: '22626',
    departureStation: { '@type': 'TrainStation', name: 'KSR Bengaluru City Junction', identifier: 'SBC' },
    arrivalStation: { '@type': 'TrainStation', name: 'MGR Chennai Central', identifier: 'MAS' },
    departureTime: '13:30',
    arrivalTime: '19:45',
    provider: { '@type': 'Organization', name: 'Indian Railways' },
    offers: { '@type': 'Offer', priceCurrency: 'INR', lowPrice: '130', highPrice: '1400' }
  },
  {
    '@context': 'https://schema.org',
    '@type': 'TrainTrip',
    name: 'SMET MAS SF Express',
    trainNumber: '12692',
    departureStation: { '@type': 'TrainStation', name: 'KSR Bengaluru City Junction', identifier: 'SBC' },
    arrivalStation: { '@type': 'TrainStation', name: 'MGR Chennai Central', identifier: 'MAS' },
    departureTime: '23:00',
    arrivalTime: '04:55',
    provider: { '@type': 'Organization', name: 'Indian Railways' },
    offers: { '@type': 'Offer', priceCurrency: 'INR', lowPrice: '230', highPrice: '1600' }
  }
];

const RouteSeoHead = () => (
  <Helmet>
    {/* Title + description overrides (curated for this route) */}
    <title>Bangalore to Chennai Trains | 30+ Trains, Timetable, Fare – MakeMyTrip</title>
    <meta
      name="description"
      content="Book Bangalore to Chennai train tickets online. 30+ trains including Vande Bharat, Shatabdi and overnight trains. Check timetable, fares from ₹130, class options and seat availability."
    />
    <meta
      name="keywords"
      content="bangalore to chennai train, bangalore chennai train ticket price, vande bharat bangalore chennai, shatabdi express bangalore chennai, tatkal train ticket bangalore chennai, SBC to MAS train, overnight train bangalore chennai, fastest train bangalore chennai, bangalore chennai train timetable, KSR bengaluru chennai SF mail, kaveri express, lalbagh sf express, bangalore chennai sleeper fare, IRCTC bangalore chennai booking"
    />

    {/* Canonical */}
    <link rel="canonical" href={PAGE_URL} />

    {/* Crawl directives + locale + geo */}
    <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
    <meta httpEquiv="content-language" content="en-IN" />
    <meta name="geo.region" content="IN-KA" />
    <meta name="geo.placename" content="Bangalore, Karnataka" />

    {/* Open Graph */}
    <meta property="og:title" content="Bangalore to Chennai Trains | Timetable, Fare & Booking – MakeMyTrip" />
    <meta
      property="og:description"
      content="Book Bangalore to Chennai train tickets. 30+ trains including Vande Bharat, Shatabdi, SF Mail and overnight trains. Fares from ₹130 to ₹5,500. Check timings and availability."
    />
    <meta property="og:url" content={PAGE_URL} />
    <meta property="og:type" content="website" />
    <meta property="og:locale" content="en_IN" />
    <meta property="og:site_name" content="MakeMyTrip" />

    {/* Twitter card */}
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:site" content="@makemytrip" />
    <meta name="twitter:title" content="Bangalore to Chennai Trains – Timings, Fare & Booking" />
    <meta
      name="twitter:description"
      content="30+ trains from Bangalore to Chennai. Book Vande Bharat, Shatabdi, overnight trains. Fares from ₹130. Check live timetable on MakeMyTrip."
    />

    {/* Structured data — existing */}
    <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
    <script type="application/ld+json">{JSON.stringify(faqPageSchema)}</script>
    <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
    <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
    <script type="application/ld+json">{JSON.stringify(datasetSchema)}</script>

    {/* Structured data — new sections */}
    <script type="application/ld+json">{JSON.stringify(nightTrainsSchema)}</script>
    <script type="application/ld+json">{JSON.stringify(morningTrainsSchema)}</script>
    <script type="application/ld+json">{JSON.stringify(fastestTrainsSchema)}</script>
    <script type="application/ld+json">{JSON.stringify(cancellationHowToSchema)}</script>
    <script type="application/ld+json">{JSON.stringify(boardingStationHowToSchema)}</script>
    <script type="application/ld+json">{JSON.stringify(tatkalDatasetSchema)}</script>
    <script type="application/ld+json">{JSON.stringify(waitlistDatasetSchema)}</script>
    <script type="application/ld+json">{JSON.stringify(arrivalStationDatasetSchema)}</script>

    {/* Structured data — TrainTrip (one per key train, most precise type for AI transport parsers) */}
    {trainTripSchemas.map((schema) => (
      <script key={schema.trainNumber} type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    ))}
  </Helmet>
);

export default RouteSeoHead;
